import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import API, { parseError } from "../utils/api";
import { Color, Role } from "../constants";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function CreateWorkOrder() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [allCustomers, setAllCustomers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [coolers, setCoolers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [routeFilter, setRouteFilter] = useState("");
  const [form, setForm] = useState({
    customer_number: "",
    task_category: "",
    cooler_type: "",
    store_contact: "",
    asm_comments: "",
    desired_completion_date: "",
  });
  const [photos, setPhotos] = useState([]);

  const role = user?.role;
  const needsRouteFilter = [
    Role.SA,
    Role.DSM,
    Role.GSM,
    Role.SUPER_ADMIN,
  ].includes(role);
  // Whether a cooler model is required is a per-category flag set in the
  // Admin Panel, not a fixed list of category names.
  const coolerCategories = categories
    .filter((c) => c.requires_cooler)
    .map((c) => c.name);
  const needsCooler = coolerCategories.includes(form.task_category);

  const getMinDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow === 6) d.setDate(d.getDate() + 2);
    if (dow === 0) d.setDate(d.getDate() + 1);
    return d;
  };

  useEffect(() => {
    Promise.all([
      API.get("/lookup/customers"),
      API.get("/lookup/categories"),
      API.get("/lookup/coolers"),
    ])
      .then(([cust, cat, cool]) => {
        setAllCustomers(cust.data);
        setCategories(cat.data);
        setCoolers(cool.data);
      })
      .catch(() => setError("Failed to load form data."));
  }, []);

  const availableRoutes = needsRouteFilter
    ? [...new Set(allCustomers.map((c) => c.route).filter(Boolean))].sort()
    : [];

  const visibleCustomers =
    needsRouteFilter && routeFilter
      ? allCustomers.filter((c) => c.route === routeFilter)
      : allCustomers;

  const selectedCustomer = allCustomers.find(
    (c) => c.customer_number === form.customer_number,
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "task_category" && !coolerCategories.includes(value)
        ? { cooler_type: "" }
        : {}),
    }));
  };

  const handlePhotoAdd = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 3 - photos.length;
    const toAdd = files.slice(0, remaining).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPhotos((prev) => [...prev, ...toAdd]);
    e.target.value = "";
  };

  const handlePhotoRemove = (index) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const payload = {
        customer_number: form.customer_number,
        task_category: form.task_category,
        cooler_type: needsCooler ? form.cooler_type : null,
        store_contact: form.store_contact || null,
        asm_comments: form.asm_comments || null,
        desired_completion_date: form.desired_completion_date || null,
      };
      const res = await API.post("/work-orders", payload);
      const orderId = res.data.id;

      for (const photo of photos) {
        const fd = new FormData();
        fd.append("file", photo.file);
        await API.post(`/work-orders/${orderId}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      navigate(`/work-orders/${orderId}?created=1`);
    } catch (err) {
      setError(parseError(err, "Failed to create work order."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate("/work-orders")}
          className="text-gray-400 hover:text-gray-600 text-sm flex items-center gap-1 mb-4"
        >
          ← Back to Work Orders
        </button>
        <h1 className="text-2xl font-bold text-gray-800">Create Work Order</h1>
        <p className="text-gray-500 text-sm mt-1">
          Fill in the details for the new work order
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-5"
      >
        {/* Route filter */}
        {needsRouteFilter && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Route
            </label>
            <select
              value={routeFilter}
              onChange={(e) => {
                setRouteFilter(e.target.value);
                setForm((prev) => ({ ...prev, customer_number: "" }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                         focus:outline-none focus:ring-2"
            >
              <option value="">All routes</option>
              {availableRoutes.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Customer */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Customer <span style={{ color: Color.RED }}>*</span>
          </label>
          <select
            name="customer_number"
            value={form.customer_number}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                       focus:outline-none focus:ring-2"
          >
            <option value="">Select a customer...</option>
            {visibleCustomers.map((c) => (
              <option key={c.customer_number} value={c.customer_number}>
                {c.name} — {c.address}
                {needsRouteFilter ? ` (Route: ${c.route})` : ""}
              </option>
            ))}
          </select>
          {selectedCustomer && (
            <p className="text-xs text-gray-400 mt-1">
              📍 {selectedCustomer.address}, {selectedCustomer.city}· #
              {selectedCustomer.customer_number}· Route:{" "}
              {selectedCustomer.route}
            </p>
          )}
        </div>

        {/* Task Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Task Category <span style={{ color: Color.RED }}>*</span>
          </label>
          <select
            name="task_category"
            value={form.task_category}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                       focus:outline-none focus:ring-2"
          >
            <option value="">Select a category...</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        {/* Cooler Type */}
        {needsCooler && (
          <div
            className="rounded-lg p-4"
            style={{ backgroundColor: "#E3F4FB", border: "1px solid #0099CC" }}
          >
            <label
              className="block text-sm font-medium mb-1"
              style={{ color: "#0077A3" }}
            >
              Cooler Model <span style={{ color: Color.RED }}>*</span>
            </label>
            <select
              name="cooler_type"
              value={form.cooler_type}
              onChange={handleChange}
              required={needsCooler}
              className="w-full px-3 py-2 border rounded-lg text-sm
                         focus:outline-none focus:ring-2 bg-white"
              style={{ borderColor: "#0099CC" }}
            >
              <option value="">Select cooler model...</option>
              {coolers.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Desired Completion Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Desired Completion Date
          </label>
          <DatePicker
            selected={
              form.desired_completion_date
                ? new Date(form.desired_completion_date + "T00:00:00")
                : null
            }
            onChange={(date) =>
              setForm((prev) => ({
                ...prev,
                desired_completion_date: date
                  ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
                  : "",
              }))
            }
            minDate={getMinDate()}
            filterDate={(date) => date.getDay() !== 0 && date.getDay() !== 6}
            dateFormat="yyyy-MM-dd"
            placeholderText="Select a date..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
             focus:outline-none focus:ring-2"
            autoComplete="off"
          />
          {!form.desired_completion_date && (
            <p className="text-xs text-gray-400 mt-1">
              If left blank, defaults to 7 days from today.
            </p>
          )}
        </div>

        {/* Photos */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Photos <span className="text-gray-400 text-xs">(up to 3)</span>
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {photos.map((p, i) => (
              <div
                key={i}
                className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200"
              >
                <img
                  src={p.preview}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => handlePhotoRemove(i)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full text-white
                             text-xs flex items-center justify-center"
                  style={{ backgroundColor: Color.RED }}
                >
                  ✕
                </button>
              </div>
            ))}
            {photos.length < 3 && (
              <label
                className="w-24 h-24 flex flex-col items-center justify-center
                                border-2 border-dashed border-gray-300 rounded-lg
                                cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition"
              >
                <svg
                  className="w-6 h-6 text-gray-400 mb-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                <span className="text-xs text-gray-400">Add photo</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handlePhotoAdd}
                />
              </label>
            )}
          </div>
        </div>

        {/* Store Contact */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Store Contact Name
          </label>
          <input
            type="text"
            name="store_contact"
            value={form.store_contact}
            onChange={handleChange}
            placeholder="e.g. Sarah, Mike..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                       focus:outline-none focus:ring-2"
          />
        </div>

        {/* Comments */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Comments / Instructions
          </label>
          <textarea
            name="asm_comments"
            value={form.asm_comments}
            onChange={handleChange}
            placeholder="Any special instructions and notes..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                       focus:outline-none focus:ring-2 resize-none"
          />
        </div>

        {error && (
          <div
            className="p-3 rounded-lg text-sm border"
            style={{
              backgroundColor: "#FDECEA",
              borderColor: "#FFCDD2",
              color: "#A00028",
            }}
          >
            {error}
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate("/work-orders")}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm
                       font-medium text-gray-600 hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white
                       transition disabled:opacity-50"
            style={{ backgroundColor: Color.NAVY }}
          >
            {loading ? "Creating..." : "Create Work Order"}
          </button>
        </div>
      </form>
    </div>
  );
}
